/*
 * File: store.ts
 * Project: beaker
 * -----
 * Last Modified: 15 Mar 2022 10:33:40 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { AnyAction, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { abbyCadabbyApi, abbyCadabbyParams } from './services/abbyCadabby';
import { cookieMonsterApi } from './services/cookieMonster';
import { freddyCadabbyApi, freddyCadabbyParams } from './services/freddyCadabby';
import { maggieCadabbyParams } from './services/maggieCadabby/legalEntitiesParams';
import { maggieCadabbyApi } from './services/maggieCadabby/maggieCadabbyApi';
import { rudyCadabbyApi } from './services/rudyCadabby/rudyCadabbyApi';
import { appSlice, businessListSlice } from './slices';

/**
 * Redux store configuration with optimized API and state slice inclusion
 * Only includes APIs and slices that are actually used in the application
 */
export const store = configureStore({
  reducer: {
    // Core application state
    [appSlice.name]: appSlice.reducer,
    [businessListSlice.name]: businessListSlice.reducer,

    // Abby Cadabby - Used for BusinessList and DivisionListItem
    [abbyCadabbyApi.reducerPath]: abbyCadabbyApi.reducer,
    [abbyCadabbyParams.name]: abbyCadabbyParams.reducer,

    // Freddy Cadabby - Used for InternalApplications and WarehouseApplications
    [freddyCadabbyApi.reducerPath]: freddyCadabbyApi.reducer,
    [freddyCadabbyParams.name]: freddyCadabbyParams.reducer,

    // Maggie Cadabby - Used for PageSubHeader (legal entities)
    [maggieCadabbyApi.reducerPath]: maggieCadabbyApi.reducer,
    [maggieCadabbyParams.name]: maggieCadabbyParams.reducer,
    [cookieMonsterApi.reducerPath]: cookieMonsterApi.reducer,

    // Rudy Cadabby - Used for DivisionApplications
    [rudyCadabbyApi.reducerPath]: rudyCadabbyApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      // Only include middleware for APIs that are actually used
      abbyCadabbyApi.middleware,
      freddyCadabbyApi.middleware,
      maggieCadabbyApi.middleware,
      rudyCadabbyApi.middleware,
      cookieMonsterApi.middleware
    ])
});

export type ReduxState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type ThunkAppDispatch = ThunkDispatch<ReduxState, void, AnyAction>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

setupListeners(store.dispatch);
export default store;
